<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

// Upstream dropped the tab bar from this sidebar at 4.3.0: Copilot moved to a
// global panel in Dashboard.vue, and notes are reachable from the contact page.
// This fork keeps a Contact/Notes tab bar here, because reading notes without
// leaving the conversation is the whole point of it.
const CONTACT_TABS_OPTIONS = [
  { key: 'CONTACT', value: 'contact' },
  { key: 'NOTES', value: 'notes' },
];

const tabs = computed(() =>
  CONTACT_TABS_OPTIONS.map(tab => ({
    label:
      tab.key === 'NOTES'
        ? t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${tab.key}`)
        : t(`CONVERSATION.SIDEBAR.${tab.key}`),
    value: tab.value,
  }))
);

const selectedTab = ref(0);

const handleTabChange = tab => {
  selectedTab.value = tabs.value.findIndex(
    tabItem => tabItem.value === tab.value
  );
};

// Upstream drives visibility off the UI setting rather than local state.
const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    return selectedTab.value;
  }
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};
</script>

<template>
  <div
    v-on-click-outside="[
      () => closeContactPanel(),
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
      },
    ]"
  >
    <div class="p-2">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="selectedTab"
        class="w-full [&>button]:w-full"
        @tab-changed="handleTabChange"
      />
    </div>
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-show="activeTab === 0"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
      <ContactNotes
        v-if="activeTab === 1"
        :contact-id="props.currentChat.meta.sender.id"
      />
    </div>
  </div>
</template>
